<i18n>
    {
        "en": {},
        "ru": {
            "notegroup": "Группа нот",
            "is_liked":  "Группа нот добавлена в Избранное!",
            "liked_already": "Группа нот уже в Избранном",
            "is_unliked": "Группа нот удалена из Избранного",
            "title_groups": "Гурманские ноты",
            "best_articles": "Лучшие статьи",
            "showmore": "Показать больше",
            "chained_pages": "Интересные подборки",
            "title": "Ноты в ароматах",
            "notes_title": "Ноты",
            "popular": "По популярности",
            "group_notes_title": "Группы нот",
            "alphabet": "По алфавиту",
            "placeholder": "Введите название ноты",
            "notes_one": "нота",
            "notes_two": "ноты",
            "notes_three": "нот",
            "description_title": "Описание",
            "tooltip_like": "Сохранить в избранное",
            "tooltip_like_users": "<br><span class=\"tooltip__note\">Понравилось {total}</span>",
            "tooltip_subscribe": "Подписаться на обновления",
            "tooltip_share": "Поделиться"
        }
    }
</i18n>

<template>
    <div class="group-page">

        <block-price-panel />

        <div class="group-page__block">
            <PageBlock v-if="isDeviceDesktop" contentVisible>
                <Breadcrumb 
                    :items="breadcrumbs"
                    :show-prev-button="showPrevBtn"
                    :prev-path-url="prevRoute"/>
                <SectionCard :loaded="loaded" sidebar="detail">

                    <template #leftbar>
                        <div class="group-page__picture-wrapper">
                            <img
                                v-if="logoUrl"
                                class="group-page__picture"
                                v-lazy-load :data-src="logoUrl"
                                data-not-lazy
                                :alt="$t('title')"/>
                            <PlaceholderImage
                                v-else
                                is-circle
                                icon-name="placeholder-note"
                                placeholder-size="263"
                                icon-size="72"/>
                        </div>

                        <PageBlock
                            v-if="group.description"
                            :smallTitle="isDeviceDesktop"
                            :title="$t('description_title')"
                            class="group-page__section-block">
                            <!-- eslint-disable-next-line -->
                            <ShowMore :max-rows="6">
                                <!-- eslint-disable-next-line vue/no-v-html -->
                                <app-content class="html-content html-content_sidebar" :html="get(group, 'description')" />
                            </ShowMore>
                        </PageBlock>

                        <PageBlock
                            v-if="relatedArticles && relatedArticles.length"
                            small-title
                            :title="$t('best_articles')">
                            <div class="perfume__page-block">
                                <ArticleRowItem
                                    :key="index + article.code"
                                    v-for="(article, index) in relatedArticles"
                                    :data="article"/>
                            </div>
                        </PageBlock>
                        <PageBlock
                            v-if="relatedPages && relatedPages.length"
                            small-title
                            :title="$t('chained_pages')">
                            <div class="perfume__page-block">
                                <LinkedPagesList :items="relatedPages"/>
                            </div>
                        </PageBlock>
                    </template>

                    <div class="group-page__rightbar"
                        slot="rightbar">

                        <PageBlock
                            class="group-page__section-block group-page__block-title"
                            id="notes_catalog"
                            small-header
                            :heading-tag="'h1'"
                            :title="compositeTitle"
                            :caption-count="notesCount | numberFormat"
                            :caption="notesCount ? getNotesDeclension : null"
                            :max-rows="6"/>

                        <div class="group-page__desktop-buttons">
                            <ButtonSquare
                                :icon="heartIcon(liked, true)"
                                class="rightbar__button"
                                v-tooltip.top="{
                                    content: likesCountTooltipPhrase, 
                                    offset: 14, 
                                    classes: 'tooltip_center'
                                }"
                                :aria-label="likesCountTooltipPhrase"
                                @click.native="handleLike"/>

                            <ButtonSquare
                                :icon="subscribeIcon(userSubscribed)"
                                class="rightbar__button"
                                :class="{'btn__subscribed_active': userSubscribed}"
                                v-tooltip.top="{content: $t('tooltip_subscribe'), offset: 14}"
                                :aria-label="$t('tooltip_subscribe')"
                                @click.native="handleSubscribe"/>

                            <ButtonSquare
                                icon="icon-share-current"
                                class="rightbar__button"
                                v-tooltip.top="{content: $t('tooltip_share'), offset: 14}"
                                :aria-label="$t('tooltip_share')"
                                @click.native="sharePage(group)"/>
                        </div>

                        <PageBlock noMargin>
                            <AromaBox :aromabox="notesGroupAromabox" />
                        </PageBlock>

                        <PageBlock
                            class="group-page__catalog"
                            content-visible
                            :counter="notesCount ? `${notesCount} ${getNotesDeclension}` : null">

                            <CatalogList
                                grid
                                search-full-bordered
                                with-sort
                                show-sort-and-filter
                                :grid-columns="6"
                                class="group-page__list"
                                id="notes_catalog"
                                :placeholder="$t('placeholder')"
                                :search-loading="queryPending"
                                :query="searchQuery"
                                @search="searchQuery = $event"
                                @change-sort-type="onChangeSortType"
                                @change-sort-order="onChangeSortOrder"
                                :with-pagination="false"
                                :items="catalogItems"
                                :sort-options="sortOptions"
                                v-model="catalogPage"
                                :page-count="catalogLastPage"
                                :sort-order="sortOrder"
                                :sort-type="sortType"
                                :lazy-loading="true"
                                :list="notesResult"
                                :limit="catalogItemsPerPage"
                                @items-updated="updateItems">

                                <template #item="{ item }" >

                                    <BaseNote
                                        :name="get(item, 'name')"
                                        :photoUrl="get(item, 'picture.url')"
                                        :url="get(item, 'htmlUrl')"
                                        :hover-shadow="true"/>

                                </template>

                            </CatalogList>
                        </PageBlock>

                        <client-only>
                            <RecentItems
                                :per-view="7"
                                class="group-page__recent"
                                :code="'noteGroup'" />
                        </client-only>
                    </div>
                </SectionCard>

            </PageBlock>
            <PageBlock v-else
                class="group-page__page-block">

                <Breadcrumb :items="breadcrumbs"
                    class="group-page__breadcrumbs"/>

                <SectionCard :loaded="loaded"
                    class="group-page__section-card"
                    content-left>

                    <div class="group-page__rightbar"
                        slot="rightbar">

                        <ButtonSquare
                            :icon="heartIcon(liked, true)"
                            class="rightbar__button"
                            aria-label="like"
                            @click.native="handleLike"/>

                        <ButtonSquare
                            :icon="userSubscribed ? 'icon-wifi-white' : 'icon-wifi-current'"
                            class="rightbar__button"
                            :class="{'btn__subscribed_active': userSubscribed}"
                            aria-label="subscribe"
                            @click.native="handleSubscribe"/>


                        <ButtonSquare
                            icon="icon-share"
                            aria-label="share"
                            @click.native="sharePage(group)"/>

                    </div>
                    <template #main>
                        <div class="group-page__picture-wrapper">
                            <img
                                v-if="logoUrl"
                                class="group-page__picture"
                                v-lazy-load :data-src="logoUrl"
                                data-not-lazy
                                :alt="$t('title')"/>
                            <PlaceholderImage
                                v-else
                                is-circle
                                icon-name="placeholder-note"
                                placeholder-size="80"/>

                            <div class="group-page__caption">
                                {{ $t('notegroup') }}
                            </div>
                        </div>

                        <PageBlock
                            class="group-page__section-block"
                            id="notes_catalog"
                            small-header
                            :title="group.name"
                            :caption-count="notesCount"
                            :caption="notesCount ? getNotesDeclension : null">
                            <!-- eslint-disable-next-line -->
                            <ShowMore :max-rows="6" class="group-page__section-block-text">
                                <!-- eslint-disable-next-line vue/no-v-html -->
                                <app-content class="html-content html-content_sidebar" :html="get(group, 'description')" />
                            </ShowMore>
                        </PageBlock>
                    </template>
                </SectionCard>

                <PageBlock>
                    <AromaBox :aromabox="notesGroupAromabox" />
                </PageBlock>

                <PageBlock
                    class="group-page__list-wrapper"
                    :counter="notesCount ? `${notesCount} ${getNotesDeclension}` : null"
                    :title="$t('notes_title')">
                    <CatalogList
                        grid
                        class="group-page__list"
                        id="notes_catalog"
                        :placeholder="$t('placeholder')"
                        :search-loading="queryPending"
                        :query="searchQuery"
                        @search="searchQuery = $event"
                        @change-sort-type="onChangeSortType"
                        @change-sort-order="onChangeSortOrder"
                        :with-pagination="false"
                        :items="catalogItems"
                        :sort-options="sortOptions"
                        v-model="catalogPage"
                        :page-count="catalogLastPage"
                        :sort-order="sortOrder"
                        :sort-type="sortType"
                        :lazy-loading="true"
                        :list="notesResult"
                        :limit="catalogItemsPerPage"
                        @items-updated="updateItems">

                        <template #item="{ item }" >

                            <BaseNote
                                :name="get(item, 'name')"
                                :photoUrl="get(item, 'picture.url')"
                                :url="get(item, 'htmlUrl')"/>

                        </template>

                    </CatalogList>
                </PageBlock>

                <client-only>
                    <RecentItems
                        class="group-page__recent"
                        :code="'note'" />
                </client-only>

                <PageBlock v-if="relatedArticles && relatedArticles.length" :title="$t('best_articles')">
                    <div class="perfume__page-block">
                        <ArticleRowItem
                            :key="index + article.code"
                            v-for="(article, index) in relatedArticles"
                            :data="article"/>
                    </div>
                </PageBlock>
                <PageBlock v-if="relatedPages && relatedPages.length" :title="$t('chained_pages')">
                    <div class="perfume__page-block">
                        <LinkedPagesList :items="relatedPages"/>
                    </div>
                </PageBlock>
            </PageBlock>

        </div>
    </div>
</template>

<script>
import get from 'lodash/get'

import { mapGetters } from 'vuex'
import ButtonSquare from '@/components/ui/buttons/ButtonSquare';
import Breadcrumb from '@/components/layout/nav/Breadcrumb'
import SectionCard from '@/components/layout/AppSectionCard';
import PageBlock from '@/components/layout/AppPageBlock';
import BlockPricePanel from '@/components/catalog/BlockPricePanel';
import AppContent from '@/components/layout/AppContent'
import AromaBox from '@/components/catalog/aromabox';
import RecentItems from '@/components/catalog/RecentItems';

import ArticleRowItem from '@/components/layout/blocks/ArticlesList/ArticleRowItem';
import LinkedPagesList from '@/components/layout/blocks/LinkedPagesList';

import NoteGroup from '@/models/catalog/NoteGroup'

import buttonActionsMixin from '@/mixins/catalog/buttonActions';
import SubscriptionsMixin from '@/mixins/catalog/subscriptions.mixins'

import { getApiQueryParams, CatalogListMixin } from '@/mixins/catalog/catalogList.mixin';
import CatalogList from '@/components/catalog/CatalogList';
import PlaceholderImage from "~/components/ui/PlaceholderImage";

export default {
    name: 'GroupNotes',
    components: {
        LinkedPagesList,
        ArticleRowItem,
        SectionCard,
        PageBlock,
        ButtonSquare,
        Breadcrumb,
        PlaceholderImage,
        BlockPricePanel,
        AppContent,
        CatalogList,
        AromaBox,
        RecentItems
    },
    mixins: [buttonActionsMixin, CatalogListMixin, SubscriptionsMixin],
    fetch() {
        if (this.$auth.loggedIn) {
            if (this.$auth.user.id) {
                const userId = this.$auth.user.id
                const group = new NoteGroup({
                    code: this.$route.params.group
                })
                const authRequests = [
                    group.userLikes().where('created_by', userId).get(),
                    ...this.getSubscriptionsRequests()
                ]

                return Promise.all(authRequests).then(results => {
                    this.liked = !!get(results, '[0][0].items[0]')
                    this.parseSubscriptions([ results[1], results[2] ])

                    this.$bus.$emit('fetch-complete')
                })
            }
        }
    },
    asyncData({ params, $auth, app, query, error }) {
        const groupApi = new NoteGroup
        const groupModel = new NoteGroup({
            code: params.group
        });

        const baseUrl = app.i18n.__baseUrl

        const catalogHref = app.router.resolve({
            name: 'Notes'
        }).href

        const filters = {
            per_page: 50
        }

        const requests = [
            groupApi.find(params.group),
            groupModel.relatedNotes().params(getApiQueryParams(query, filters)).get(),
            groupModel.relatedArticles().get(),
            groupModel.relatedPages().get(),
            app.$api.get(`/note-groups/${params.group}/aroma-boxes`)
        ];

        return Promise.all(requests).then((results) => {
            const group = get(results, '[0]')
            const notesResult = get(results, '[1][0]')
            const catalogItems = get(notesResult, 'items')
            const catalogLastPage = get(notesResult, 'lastPage')

            const relatedArticles = results[2];
            const relatedPages = results[3];

            const catalogPage = getApiQueryParams(query, filters).page;
            const searchQuery = getApiQueryParams(query, filters).name;
            const sortType = getApiQueryParams(query, filters).sort ? getApiQueryParams(query, filters).sort : 'popularity';
            const sortOrder = getApiQueryParams(query, filters).order;

            const notesGroupAromabox = get(results, '[4].data', {})

            const breadcrumbs = [
                {
                    path: baseUrl,
                    name: app.i18n.t('breadcrumbs.perfumes'),
                },
                {
                    path: baseUrl + catalogHref,
                    name: app.i18n.t('breadcrumbs.notes'),
                },
                {
                    path: baseUrl + catalogHref + group.name,
                    name: group.name,
                }
            ];

            if (process.server) {
                app.store.dispatch('rest/headers/filterLastModified', {
                    url: [
                        `/note-groups/${group.code}`,
                        `/note-groups/${group.code}/notes`
                    ]
                });
            }

            return {
                group,
                catalogPage,
                searchQuery,
                catalogLastPage,
                sortType,
                sortOrder,
                notesResult,
                catalogItems,
                relatedArticles,
                relatedPages,
                breadcrumbs,
                nextPage: get(results, '[0][0].nextPage', null),
                prevPage: get(results, '[0][0].prevPage', null),
                notesGroupAromabox
            }
        }).catch((e) => {
            error({statusCode: e.response.status, message: e.response.data});
        });
    },
    data() {
        return {
            groupLiked: false,
            group: {},
            queryPending: false,
            searchQuery: null,
            loaded: false,
            liked: false,
            breadcrumbs: [],
            relatedArticles: null,
            catalogLastPage: 12,
            relatedPages: null,
            catalogPage: 1,
            sortType: 'popularity',
            sortOrder: 'desc',
            filters: {},
            sortOptions: {
                popularity: {
                    name: this.$t('popular')
                },
                name: {
                    name: this.$t('alphabet')
                },
            },
            catalogItems: [],
            notesResult: null,
            prevRoute: null,
            catalogItemsPerPage: 50,
            isLikeProcess: false,
            notesGroupAromabox: {}
        }
    },
    computed: {
        ...mapGetters([ 'getNoImage', 'isDeviceDesktop' ]),
        entityModel() {
            return new NoteGroup({
                note_group_code: this.entityCode
            })
        },
        entityCode() {
            return this.group.code
        },
        entityTag() {
            return this.group.entityTag
        },
        logoUrl() {
            return get(this.group, 'picture.url', '')
        },
        catalogQueryObject() {
            return new NoteGroup({
                code: this.$route.params.group
            }).relatedNotes()
        },
        notesCount() {
            return get(this.notesResult, 'total', null)
        },
        notesDeclension() {
            return [
                this.$t('notes_one'),
                this.$t('notes_two'),
                this.$t('notes_three')
            ]
        },
        getNotesDeclension() {
            return this.$options.filters.numberDeclension(this.notesCount, this.notesDeclension, false);
        },
        groupsMaxHeight() {
            return 196
        },
        showPrevBtn() {
            return this.prevRoute !== null
        },
        likesCountTooltipPhrase() {
            const likes = this.get(this.group, 'likes.total', 0);

            if (likes < 1) {
                return this.$t('tooltip_like');
            }

            return this.$t('tooltip_like') + this.$t('tooltip_like_users', {
                total: this.$options.filters.numberDeclension(likes, this.$t('declensions.for_users'))
            });
        },
        compositeTitle(){
            return `<span class="page-block__title-sup">${this.$t('notegroup')}</span> ${get(this.group, 'name', '')}`
        }
    },
    watch: {
        itemsTotal(value){
            this.notesResult.total = value
        }
    },
    fetchOnServer: false,
    mounted() {
        this.loaded = true;
    },
    globalBus: {
        async 'like'(value) {
            const notices = {
                'onLiked': this.$t("is_liked"),
                'onUnliked': this.$t("is_unliked"),
                'onLikeAlready': this.$t("liked_already"),
            };
            this.liked = await this.onActionLike(value, new NoteGroup({code: this.$route.params.group}), notices)
            this.isLikeProcess = false;
        },
        'auth-complete'() {
            this.$fetch()
        }
    },
    methods: {
        get,
        handleLike() {
            if(!this.isLikeProcess){
                this.isLikeProcess = true
                this.$authEmit('like', !this.liked);
            }
        },
        handleSubscribe() {
            this.$authEmit('subscribe', {
                type: 'noteGroup',
                entity: {
                    name: this.group.name,
                    code: this.entityCode,
                    picture: get(this.group, 'picture.url')
                },
                entityTag: this.entityTag
            });
        },
        onChangeSortType(value) {
            this.sortType = value
        },
        onChangeSortOrder(value) {
            this.sortOrder = value
        },
        updateItems(items){
            this.catalogItems = [
                ...this.catalogItems,
                ...items
            ]
        }
    },
    head() {
        return {
            title: get(this.group, 'meta.title'),
            meta: this.$options.filters.metaApiExtract(this.group),
            link: [
                {
                    rel: 'canonical',
                    href: get(this.group, 'meta.canonical')
                }
            ]
        }
    },
    beforeRouteEnter(to, from, next) {
        next((vm) => {
            if(from.name === 'users.favorites') {
                vm.prevRoute = from.path
            } else {
                vm.prevRoute = null
            }
        })
    }
}
</script>

<style lang="less">
.group-page {

    @{mobile} & {
        background: @color-bg-gray;
    }

    @{desktop} & {
        &__recent{
            .page-block_recent{
                margin-top: rem(80);
                margin-bottom: rem(40);
            }
        }
    }

    &__page-block {
        background: none;
    }

    &__breadcrumbs {
        background: @color-white;
        &.breadcrumb{
            margin-bottom: 0;
        }
    }

    &__section-card {
        margin-bottom: rem(20);
        padding-top: rem(10);
    }

    &__section-block {
        &-text{
            padding-left: rem(20);
            padding-right: rem(20);
            padding-bottom: rem(20);
        }
    }

    &__button {
        margin-right: auto;
    }

    &__group {
        border-bottom: rem(1) solid @color-gray-white;
    }

    &__picture {
        --size: rem(80);

        display: block;

        border-radius: 50%;

        height: var(--size);
        width: var(--size);

        @{desktop} & {
            --size: rem(263);

            margin-bottom: 0;
        }

        &-wrapper {
            .page-padding();

            @{desktop} & {
                border: rem(1) solid @color-gray-white;

                margin-bottom: rem(38);
                padding: rem(40);
            }
        }
    }

    &__caption {
        // color: @color-gray-light;
        color: @color-gray-contrast;
        font-size: rem(15);

        margin-bottom: rem(4);
        padding-top: rem(12);

        @{desktop} & {
            padding-top: 0;
        }
    }

    &__rightbar {
        button + button {
            margin-top: rem(12);

            @{desktop} & {
                margin-top: 0;
            }
        }
    }

    &__desktop-buttons {
        display: flex;

        position: absolute;
        top: 0;
        right: 0;

        .rightbar__button {
            margin-left: rem(12);

            position: relative;
        }
    }

    &__block-title {


        @{desktop} & .page-block__header {
            margin-bottom: rem(14);
            padding: 0;
        }
    }

    &__catalog {

        @{desktop} & {
            padding-top: rem(26);

            .catalog-list__list {
                padding-top: rem(24);
                padding-left: rem(12);
            }
        }
    }

    &__list {

        &-wrapper {

            .page-block__header {
                padding-bottom: rem(8);
            }
        }

        @{desktop} & {
            --grid-item-height: rem(98);
        }
    }

    &__description {

        @{desktop} & {
            padding-top: rem(4);
        }
    }
}
</style>
